import { ReactNode, useEffect, useState, useRef } from 'react';
import { IoMdClose } from 'react-icons/io';

interface PopupWrapperProps {
  title: ReactNode;
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full';
  className?: string;
  logo?: ReactNode;
  headerComponent?: ReactNode;
}

const PopupWrapper: React.FC<PopupWrapperProps> = ({
  title,
  isOpen,
  onClose,
  children,
  size = 'md',
  className = '',
  logo,
  headerComponent,
}) => {
  // Animation state
  const [animationState, setAnimationState] = useState<'closed' | 'opening' | 'open' | 'closing'>(
    'closed'
  );

  // Ref to track previous isOpen state
  const wasOpen = useRef(isOpen);

  // Handle direct closing without animation when isOpen changes from true to false
  useEffect(() => {
    if (wasOpen.current && !isOpen) {
      // If popup was open and is now closed, immediately set to closed state
      setAnimationState('closed');
      document.body.style.overflow = 'auto';
    }

    wasOpen.current = isOpen;
  }, [isOpen]);

  // Handle ESC key press to close popup
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        handleClose();
      }
    };

    if (isOpen && animationState === 'closed') {
      setAnimationState('opening');
      setTimeout(() => setAnimationState('open'), 10); // Start animation after DOM update
      document.addEventListener('keydown', handleEscKey);
      // Prevent scrolling on body when popup is open
      document.body.style.overflow = 'hidden';
    }

    // Cleanup
    return () => {
      document.removeEventListener('keydown', handleEscKey);
      // Only restore scroll if animation state is closed and isOpen is false
      if (animationState === 'closing' || animationState === 'closed') {
        document.body.style.overflow = 'auto';
      }
    };
  }, [isOpen, animationState]);

  // Handle close with animation
  const handleClose = () => {
    setAnimationState('closing');
    // Immediately restore scrolling when starting to close
    document.body.style.overflow = 'auto';

    setTimeout(() => {
      setAnimationState('closed');
      onClose();
    }, 300); // Match the CSS transition duration
  };

  // If popup is not open and animation is closed, don't render anything
  if (!isOpen && animationState === 'closed') return null;

  // Define width based on size prop - 70% for PC screens for larger sizes
  const sizeClasses = {
    sm: 'w-[95%] sm:w-[400px]',
    md: 'w-[95%] sm:w-[550px]',
    mlg: 'w-[95%] sm:w-[70%] max-w-[750px]',
    lg: 'w-[95%] sm:w-[70%] max-w-[900px]',
    xl: 'w-[95%] sm:w-[70%] max-w-[1200px]',
    full: 'w-[95%] sm:w-[70%] max-w-[90%]',
  };

  // Animation classes - Moving from bottom to top without opacity changes
  const animationClasses = {
    opening: 'translate-y-[80vh]',
    open: 'translate-y-0',
    closing: 'translate-y-[80vh]',
    closed: 'translate-y-[80vh]',
  };

  return (
    <div className="fixed inset-0 flex items-center justify-center z-50">
      {/* Backdrop with animation */}
      <div
        className={`fixed inset-0 bg-black transition-opacity duration-300 ease-in-out ${animationState === 'open' ? 'bg-opacity-50' : 'bg-opacity-0'
          }`}
        onClick={handleClose}
      />

      {/* Popup Content - Centered with rounded corners on all sides */}
      <div
        className={`bg-white rounded-2xl shadow-xl transform transition-all duration-300 ease-in-out p-10 
          ${sizeClasses[size]} mx-auto my-auto relative z-10 ${className} ${animationClasses[animationState]}
          max-h-[90vh] overflow-y-auto`}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center py-4 flex-grow">
            {logo && <div className="mr-4 w-16 h-16">{logo}</div>}
            <h2 className="text-4xl font-semibold text-gray-800">{title}</h2>
            {headerComponent && <div className="mr-auto ml-4">{headerComponent}</div>}
          </div>
          <button
            onClick={handleClose}
            className="p-1.5 rounded-full hover:bg-gray-100 transition-colors text-gray-500 hover:text-gray-700 focus:outline-none"
            aria-label="Close popup"
          >
            <IoMdClose size={38} />
          </button>
        </div>

        {/* Scrollable Content */}
        <div>{children}</div>
      </div>
    </div>
  );
};

export default PopupWrapper;
